/**
 * Gate System Configuration
 * Single source of truth for all freemium gating rules
 */

// Action codes — must match backend GATE_ACTIONS
export const GATE_ACTIONS = {
  QUICK_ESTIMATE: 'quick_estimate',
  COMPLETE_HOME_ESTIMATE: 'complete_home_estimate',
  MATERIAL_ESTIMATE: 'material_estimate',
  SAVE_PROJECT: 'save_project',
  COMPARE_SCENARIOS: 'compare_scenarios',
  EXPORT_PDF: 'export_pdf',
  ADD_EXPENSE: 'add_expense',
  BUDGET_ALLOCATE: 'budget_allocate',
};

// Per-action limits for each tier
// -1 = unlimited, 0 = blocked (requires signup/upgrade)
export const GATE_LIMITS = {
  guest: {
    quick_estimate: 3,
    complete_home_estimate: 1,
    material_estimate: 2,
    save_project: 0,        // blocked → signup
    compare_scenarios: 0,   // blocked → signup
    export_pdf: 0,          // blocked → signup
    add_expense: 3,         // 3 expenses per day for guests
    budget_allocate: 1,     // 1 budget allocation per day for guests
  },
  free: {
    quick_estimate: 5,
    complete_home_estimate: 3,
    material_estimate: 5,
    save_project: 1,
    compare_scenarios: 0,   // blocked → subscribe (planning_pass+)
    export_pdf: 0,          // blocked → subscribe (builder_pass)
    add_expense: 10,        // 10 expenses per day for free users
    budget_allocate: 3,     // 3 budget allocations per day for free users
  },
  paid: {
    quick_estimate: -1,
    complete_home_estimate: -1,
    material_estimate: -1,
    save_project: -1,
    compare_scenarios: -1,
    export_pdf: -1,
    add_expense: -1,
    budget_allocate: -1,
  },
};

// Minimum pass required for boolean-gated features
export const MINIMUM_PASS = {
  compare_scenarios: 'planning_pass',
  export_pdf: 'builder_pass',
};

// Gate denial reasons
export const GATE_REASONS = {
  SIGNUP_REQUIRED: 'SIGNUP_REQUIRED',
  LIMIT_REACHED: 'LIMIT_REACHED',
  PASS_REQUIRED: 'PASS_REQUIRED',
  LOADING: 'LOADING',
};

// LocalStorage keys for guest usage tracking
export const GUEST_STORAGE_KEY = 'rumahkira_guest_usage';

/**
 * Get guest usage from localStorage
 * @returns {{ [actionCode]: number, resetAt: number }}
 */
export function getGuestUsage() {
  try {
    const raw = localStorage.getItem(GUEST_STORAGE_KEY);
    if (!raw) return resetGuestUsage();

    const data = JSON.parse(raw);
    // Check 24h reset
    if (data.resetAt && Date.now() - data.resetAt >= 24 * 60 * 60 * 1000) {
      return resetGuestUsage();
    }
    return data;
  } catch {
    return resetGuestUsage();
  }
}

/**
 * Reset guest usage counters
 */
export function resetGuestUsage() {
  const data = { resetAt: Date.now() };
  Object.values(GATE_ACTIONS).forEach(code => { data[code] = 0; });
  try {
    localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(data));
  } catch { /* ignore */ }
  return data;
}

/**
 * Increment guest usage for an action
 */
export function incrementGuestUsage(actionCode) {
  const data = getGuestUsage();
  data[actionCode] = (data[actionCode] || 0) + 1;
  try {
    localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(data));
  } catch { /* ignore */ }
  return data;
}

/**
 * Determine gate result for a given action + user state (client-side)
 * @param {string} actionCode
 * @param {{ isAuthenticated: boolean, isPassActive: boolean, serverActionLimits: object|null }} state
 * @returns {{ allowed: boolean, reason: string|null, used: number, limit: number, remaining: number, minimumPass: string|null }}
 */
export function evaluateGate(actionCode, { isAuthenticated, isPassActive, serverActionLimits }) {
  // Paid user with active pass — use server limits if available
  if (isAuthenticated && isPassActive) {
    if (serverActionLimits && serverActionLimits[actionCode]) {
      return { ...serverActionLimits[actionCode], reason: null };
    }
    return { allowed: true, reason: null, used: 0, limit: -1, remaining: -1, minimumPass: null };
  }

  // Logged-in free user — use server limits if available, else local config
  if (isAuthenticated) {
    if (serverActionLimits && serverActionLimits[actionCode]) {
      const data = serverActionLimits[actionCode];
      if (!data.allowed) {
        const reason = data.minimum_pass ? GATE_REASONS.PASS_REQUIRED : GATE_REASONS.LIMIT_REACHED;
        return { ...data, reason, minimumPass: data.minimum_pass || MINIMUM_PASS[actionCode] || null };
      }
      return { ...data, reason: null, minimumPass: null };
    }
    // Fallback to local free limits
    const limit = GATE_LIMITS.free[actionCode];
    if (limit === 0) {
      return {
        allowed: false,
        reason: GATE_REASONS.PASS_REQUIRED,
        used: 0,
        limit: 0,
        remaining: 0,
        minimumPass: MINIMUM_PASS[actionCode] || null,
      };
    }
    return { allowed: true, reason: null, used: 0, limit, remaining: limit, minimumPass: null };
  }

  // Guest — track locally
  const guestUsage = getGuestUsage();
  const limit = GATE_LIMITS.guest[actionCode];
  const used = guestUsage[actionCode] || 0;

  if (limit === 0) {
    return {
      allowed: false,
      reason: GATE_REASONS.SIGNUP_REQUIRED,
      used: 0,
      limit: 0,
      remaining: 0,
      minimumPass: null,
    };
  }

  if (used >= limit) {
    return {
      allowed: false,
      reason: GATE_REASONS.SIGNUP_REQUIRED,
      used,
      limit,
      remaining: 0,
      minimumPass: null,
    };
  }

  return {
    allowed: true,
    reason: null,
    used,
    limit,
    remaining: limit - used,
    minimumPass: null,
  };
}

// Human-readable action labels (bilingual)
export const ACTION_LABELS = {
  en: {
    quick_estimate: 'Quick Estimates',
    complete_home_estimate: 'Complete Home Estimates',
    material_estimate: 'Material Calculations',
    save_project: 'Save Projects',
    compare_scenarios: 'Compare Scenarios',
    export_pdf: 'PDF Exports',
    add_expense: 'Expense Entries',
    budget_allocate: 'Budget Allocations',
  },
  my: {
    quick_estimate: 'Anggaran Pantas',
    complete_home_estimate: 'Anggaran Rumah Lengkap',
    material_estimate: 'Pengiraan Bahan',
    save_project: 'Simpan Projek',
    compare_scenarios: 'Bandingkan Senario',
    export_pdf: 'Eksport PDF',
    add_expense: 'Catatan Perbelanjaan',
    budget_allocate: 'Peruntukan Bajet',
  },
};

// Bilingual labels for usage alerts
export const USAGE_ALERT_TEXTS = {
  en: {
    remaining: '{remaining} of {limit} {action} remaining today',
    lastOne: 'Last {action} for today! Upgrade for unlimited access.',
    limitReached: 'Daily limit reached for {action}. Resets in {hours}h.',
    limitReachedGuest: 'Free {action} used up. Sign up for more!',
    unlimited: 'Unlimited {action}',
    upgrade: 'Upgrade',
    signUp: 'Sign Up',
  },
  my: {
    remaining: '{remaining} daripada {limit} {action} berbaki hari ini',
    lastOne: '{action} terakhir untuk hari ini! Naik taraf untuk akses tanpa had.',
    limitReached: 'Had harian dicapai untuk {action}. Set semula dalam {hours}j.',
    limitReachedGuest: '{action} percuma telah habis. Daftar untuk lebih!',
    unlimited: '{action} tanpa had',
    upgrade: 'Naik Taraf',
    signUp: 'Daftar',
  },
};

// Bilingual labels for gate reasons
export const GATE_TEXTS = {
  en: {
    // Signup view
    signupTitle: 'Create Your Account',
    signupSubtitle: 'Sign up to continue using RumahKira',
    signupSubtitleLimit: "You've used your free calculations. Sign up to get more!",
    signUp: 'Create Free Account',
    signIn: 'Already have an account? Sign In',
    // Limit view
    limitTitle: 'Daily Limit Reached',
    limitSubtitle: "You've used {used} of {limit} daily calculations. Upgrade for unlimited access!",
    // Pass required view
    passTitle: 'Access Pass Required',
    passSubtitle: 'This feature requires a {pass} or higher',
    upgrade: 'View Access Pass Plans',
    // Common
    maybeLater: 'Maybe Later',
    benefits: [
      'Unlimited calculations',
      'Save and access projects anytime',
      'Export detailed PDF reports',
      'Complete material calculator',
      'Compare scenarios side by side',
    ],
    poweredBy: 'RumahKira\u2122 by Kondingau Community Insight (KCi)',
    // Pass names for display
    passNames: {
      quick_pass: 'Quick Pass',
      planning_pass: 'Planning Pass',
      builder_pass: 'Builder Pass',
    },
  },
  my: {
    signupTitle: 'Cipta Akaun Anda',
    signupSubtitle: 'Daftar untuk terus menggunakan RumahKira',
    signupSubtitleLimit: 'Anda telah menggunakan pengiraan percuma. Daftar untuk mendapat lebih!',
    signUp: 'Cipta Akaun Percuma',
    signIn: 'Sudah ada akaun? Log Masuk',
    limitTitle: 'Had Harian Dicapai',
    limitSubtitle: 'Anda telah menggunakan {used} daripada {limit} pengiraan harian. Naik taraf untuk akses tanpa had!',
    passTitle: 'Perlu Pas Akses',
    passSubtitle: 'Ciri ini memerlukan {pass} atau lebih tinggi',
    upgrade: 'Lihat Pelan Pas Akses',
    maybeLater: 'Mungkin Nanti',
    benefits: [
      'Pengiraan tanpa had',
      'Simpan dan akses projek bila-bila masa',
      'Eksport laporan PDF terperinci',
      'Kalkulator bahan lengkap',
      'Bandingkan senario bersebelahan',
    ],
    poweredBy: 'RumahKira\u2122 oleh Kondingau Community Insight (KCi)',
    passNames: {
      quick_pass: 'Pas Pantas',
      planning_pass: 'Pas Perancangan',
      builder_pass: 'Pas Pembina',
    },
  },
};
